import { useState } from 'react'
import type { ReactNode } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { Icon, Page, SideNavigation } from '@loadsmart/miranda-react'
import { useSettings } from '@loadsmart/react-frontend-settings'

import logo from 'assets/images/analytics_portal_logo.svg'
import { useSession } from 'auth/ui/hooks/useSession'
import { RequestType } from 'core/domain/RequestType'
import type { PBIReport } from 'managed-analytics/domain/PBIReport'
import { getPBIReportsQueryOptions } from 'managed-analytics/use-cases/getPBIReports'

import ZendeskDialog from './zendesk/TryNowZendeskDialog'

export const StyledContainer = styled.div`
  height: 100vh;
`

export type AppLayoutProps = {
  children: ReactNode
}

export default function AppLayout({ children }: AppLayoutProps) {
  const session = useSession()
  const { isLoading, values } = useSettings(['flags.SCHEDULER_ENABLED'])
  const isSchedulerEnabled = Boolean(values?.[0])

  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const location = useLocation()

  return (
    <StyledContainer>
      <Page>
        <SideNavigation>
          <SideNavigation.Header>
            <img src={logo} alt="Analytics Portal" />
          </SideNavigation.Header>
          <SideNavigation.Body>
            <SideNavigation.Item
              href="/demo"
              leading={<Icon name="graph" color="color-primary-20" />}
              active={location.pathname === '/demo'}
            >
              Analytics Library
            </SideNavigation.Item>
            {session && (
              <SideNavigation.Item
                href="/integrations"
                leading={<Icon name="signal" color="color-primary-20" />}
                active={location.pathname === '/integrations'}
              >
                Integrations
              </SideNavigation.Item>
            )}
            {session && !isLoading && isSchedulerEnabled && (
              <SideNavigation.Item
                href="/schedule"
                leading={<Icon name="calendar" color="color-primary-20" />}
                active={location.pathname === '/schedule'}
              >
                Scheduler
              </SideNavigation.Item>
            )}
            {session && (
              <>
                <SideNavigation.Divider />
                <SideNavigationReports />
              </>
            )}
          </SideNavigation.Body>
          <SideNavigation.Footer>
            <SideNavigation.Item
              leading={<Icon name="chat" color="color-primary-20" />}
              onClick={() => {
                setIsZendeskDialogOpen(true)
              }}
            >
              Talk to sales
            </SideNavigation.Item>
          </SideNavigation.Footer>
        </SideNavigation>

        {children}
      </Page>

      <ZendeskDialog
        requestType={RequestType.Question}
        open={isZendeskDialogOpen}
        onClose={() => setIsZendeskDialogOpen(false)}
      />
    </StyledContainer>
  )
}

export function SideNavigationReports() {
  const location = useLocation()

  const { data } = useSuspenseQuery(getPBIReportsQueryOptions())

  const reportsByProfile = data.reduce(
    (acc, report) => {
      acc[report.profile] = [...(acc[report.profile] || []), report]
      return acc
    },
    {} as Record<string, PBIReport[]>
  )

  return Object.entries(reportsByProfile).map(([profile, reports]) => (
    <SideNavigation.SubNav key={profile} expanded>
      <SideNavigation.SubNavTitle>{profile}</SideNavigation.SubNavTitle>
      <SideNavigation.SubNavContent>
        {reports.map(report => (
          <SideNavigation.SubNavItem
            key={report.uuid}
            href={`/reports/${report.uuid}?rls_filter=${encodeURIComponent(report.rlsFilter)}&customer_type=${encodeURIComponent(report.customerType)}&role_name=${encodeURIComponent(report.roleName)}&profile=${encodeURIComponent(report.profile)}`}
            active={location.pathname.startsWith(`/reports/${report.uuid}`)}
          >
            {report.reportName}
          </SideNavigation.SubNavItem>
        ))}
      </SideNavigation.SubNavContent>
    </SideNavigation.SubNav>
  ))
}
